import { useCallback, isValidElement, useEffect } from 'react';
// Комопнент обертка над Field, разрешаем импорт
// eslint-disable-next-line no-restricted-imports
import { Field, FieldInputProps, FieldMetaState, FieldProps, SupportedInputs } from 'react-final-form';
import { FieldState } from 'final-form';

import { isNumber } from 'Utils/Utils';
import { useCurrentStepContext } from 'src/components/VacancyCreate/contexts/CurrentStepContext';
import { useFieldRulesValidator } from 'src/components/VacancyCreate/hooks/useFieldRulesValidator';
import { shouldShowError } from 'src/components/VacancyCreate/utils';

export interface FieldWrapperMetaState<FieldValue> extends FieldMetaState<FieldValue> {
    showError?: boolean;
}

export interface FieldWrapperInputProps<InputValue> extends FieldInputProps<InputValue> {
    invalid?: boolean;
    maxLength?: number;
}

export interface FieldWrapperRenderProps<FieldValue, InputValue = FieldValue> {
    meta: FieldWrapperMetaState<FieldValue>;
    input: FieldWrapperInputProps<InputValue>;
    [p: string]: unknown;
}

export interface FieldWrapperProps<FieldValue, InputValue = FieldValue>
    extends FieldProps<FieldValue, FieldWrapperRenderProps<FieldValue, InputValue>, HTMLElement, InputValue> {
    validate?: (value: FieldValue, allValues: object, meta?: FieldState<FieldValue>) => unknown;
    validateFields?: string[];
    render?: (props: FieldWrapperRenderProps<FieldValue, InputValue>) => React.ReactNode;
    children?: ((props: FieldWrapperRenderProps<FieldValue, InputValue>) => React.ReactNode) | React.ReactNode;

    /** @deprecated Проп component не поддерживается */
    component?: React.ComponentType<FieldWrapperRenderProps<FieldValue, InputValue>> | SupportedInputs;
}

/*
 * Компонент обертка над Field из rff,
 * вводит общую логику валидации и отображения ошибок,
 * регистрирует контекст рендера
 * */
function FieldWrapper<FieldValue, InputValue = FieldValue>({
    name,
    validate,
    validateFields = [],
    render,
    children,
    ...props
}: FieldWrapperProps<FieldValue, InputValue>) {
    const { stepName, setToRenderedFieldsRef, deleteFromRenderedFieldsRef } = useCurrentStepContext();

    const { rules, validateFieldByRules } = useFieldRulesValidator({ fieldName: name });

    useEffect(() => {
        if (stepName) {
            setToRenderedFieldsRef(name);
        } else {
            deleteFromRenderedFieldsRef(name);
        }

        return () => {
            deleteFromRenderedFieldsRef(name);
        };
    }, [deleteFromRenderedFieldsRef, name, setToRenderedFieldsRef, stepName]);

    const modifyRenderProps = useCallback(
        (renderProps: FieldWrapperRenderProps<FieldValue, InputValue>) => {
            const { meta, input } = renderProps;

            const showError = shouldShowError(meta);

            return {
                input: {
                    ...input,
                    ...(isNumber(rules.maxLength) ? { maxLength: rules.maxLength } : {}),
                    invalid: showError,
                },
                meta: { ...meta, showError },
            };
        },
        [rules.maxLength]
    );

    const _render = useCallback(
        (renderProps: FieldWrapperRenderProps<FieldValue, InputValue>) => {
            if (!render) {
                return null;
            }

            return render(modifyRenderProps(renderProps));
        },
        [modifyRenderProps, render]
    );

    const _children = useCallback(
        (renderProps: FieldWrapperRenderProps<FieldValue, InputValue>) => {
            if (typeof children === 'function') {
                return children(modifyRenderProps(renderProps));
            }

            if (isValidElement(children)) {
                return children;
            }

            return null;
        },
        [children, modifyRenderProps]
    );

    const validator = useCallback(
        (value: FieldValue, allValues: object, meta?: FieldState<FieldValue>) => {
            if (validate) {
                return validate(value, allValues, meta);
            }

            return validateFieldByRules(value, allValues);
        },
        [validate, validateFieldByRules]
    );

    return (
        <Field
            name={name}
            validate={validator}
            validateFields={validateFields}
            {...props}
            render={render ? _render : undefined}
            component={undefined}
        >
            {children ? _children : undefined}
        </Field>
    );
}

export default FieldWrapper;
